import * as React from 'react'
import { getFromStorage, setInStorage } from '../storage'

interface BooleanValueHookActions {
  toggleValue: () => boolean
  setValue: React.Dispatch<React.SetStateAction<boolean>>
}

/** This hook can be used to power a single-input control, like a checkbox.
 * Optionally persist the user's preference in LocalStorage
 * Use the memoized `toggleValue` method as a plug-and-play handler, for example:
 * ```
 *  const [isOn, { toggleValue: toggleIsOn }] = useBooleanValue(false) // switch is off by default
 *  ...
 *  <Switch onChange={toggleIsOn} value={isOn} />
 * ```
 */
export function useBooleanValue(
  initialValue = false,
  options?: {
    storageKey: string
    userScoped?: boolean
  }
): [boolean, Partial<BooleanValueHookActions>] {
  const useStorage = !!options?.storageKey
  const [value, setValue] = React.useState(initialValue)

  const toggleValue = React.useCallback(() => {
    const oldValue = value
    setValue(!oldValue)
    return !oldValue
  }, [value])

  React.useEffect(() => {
    if (!useStorage) return

    const lastValueData = getFromStorage(options.storageKey, !!options?.userScoped)
    if (lastValueData) {
      setValue(lastValueData.storedValue)
    }
  }, [])

  React.useEffect(() => {
    if (!useStorage) return

    setInStorage(options.storageKey, { storedValue: value }, options.userScoped)
  }, [options, value])

  return [value, { setValue, toggleValue }]
}
